import type { AluminiumAISampleComposition } from 'api/schemas/aluminiumAISampleComposition';
import type { AluminiumComposition } from 'api/schemas/aluminiumComposition';
import { ChildSpecType } from 'api/schemas/childSpecType';
import type { EvaluatedSpecification } from 'api/schemas/evaluatedSpecification';
import type { HDPEComposition } from 'api/schemas/hDPEComposition';
import { Operand } from 'api/schemas/operand';
import type { PETComposition } from 'api/schemas/pETComposition';
import type { SimpleEvaluatedSpecification } from 'api/schemas/simpleEvaluatedSpecification';
import type { I18nLanguage, I18nScopedKey, I18nTranslateFunction } from 'i18n';
import { translate } from 'i18n';
import type { ComponentProps } from 'react';

import { MaterialBreakdownItem } from '../../components/MaterialBreakdownItem/MaterialBreakdownItem';
import { formatComparisonThreshold } from '../formatComparisonThreshold/formatComparisonThreshold';
import { formatPercentageFeature, isZeroOrSmallForDisplay } from '../formatFeature/formatFeature';
import { formatInstructionComparisonOperator } from '../formatInstructionComparisonOperator/formatInstructionComparisonOperator';
import { formatInstructionOperand } from '../formatInstructionOperand/formatInstructionOperand';
import { formatOperandsList } from '../formatOperandsList/formatOperandsList';
import { formatThreshold } from '../formatThreshold/formatThreshold';
import { getOperandValue } from '../getOperandValue/getOperandValue';
import { getChildSpecificationKind, isRootSpecification } from '../specificationGrouping/specificationGrouping';

type SpecItem = ComponentProps<typeof MaterialBreakdownItem> & {
  isLastInGroup?: boolean;
  specId?: number;
};

type EvaluatedSpecificationLike = EvaluatedSpecification | SimpleEvaluatedSpecification;
type FeaturesTranslate = I18nTranslateFunction<I18nScopedKey<'features'>>;

export type EvaluatedSpecificationComposition =
  | AluminiumComposition
  | PETComposition
  | AluminiumAISampleComposition
  | HDPEComposition;
type CompositionEntry = [Operand, number];

const INDENT_LEVEL = {
  group: 0,
  groupOperand: 1,
  subGroup: 1,
  subGroupOperand: 2,
  ungroupedOperand: 1,
} as const;

const isOperandKey = (value: string): value is Operand => Object.values(Operand).some((operand) => operand === value);

const getValuePresentation = (value: number | null) => {
  const normalizedValue = value ?? 0;
  const hasVisibleValue = !isZeroOrSmallForDisplay(normalizedValue);

  return {
    hasVisibleValue,
    value: formatPercentageFeature(normalizedValue),
  };
};

const getSpecificationSecondaryValue = (specification: EvaluatedSpecificationLike) => {
  return (
    formatComparisonThreshold(specification.spec_comparison_operator, specification.spec_threshold_value) ?? undefined
  );
};

const addSpecificationOperands = ({
  composition,
  groupId,
  indentLevel,
  items,
  operandSpecMap,
  operands,
  hideZeroOrSmallValues,
  t,
}: {
  composition?: EvaluatedSpecificationComposition;
  groupId: number;
  hideZeroOrSmallValues: boolean;
  indentLevel: number;
  items: SpecItem[];
  operandSpecMap?: Map<Operand, EvaluatedSpecificationLike>;
  operands: Operand[];
  t?: FeaturesTranslate;
}) => {
  const sortedOperands = [...operands].sort((leftOperand, rightOperand) => {
    const leftValue = getOperandValue(leftOperand, composition);
    const rightValue = getOperandValue(rightOperand, composition);

    if (leftValue !== null && rightValue !== null) {
      return rightValue - leftValue;
    }

    if (leftValue !== null && rightValue === null) {
      return -1;
    }
    if (rightValue !== null && leftValue === null) {
      return 1;
    }

    return 0;
  });

  sortedOperands.forEach((operand) => {
    const operandValue = getOperandValue(operand, composition);
    if (operandValue === null) {
      return;
    }
    if (hideZeroOrSmallValues && isZeroOrSmallForDisplay(operandValue)) {
      return;
    }

    const operandSpec = operandSpecMap?.get(operand);
    const operandSecondaryValue =
      operandSpec && operandSpec.spec_threshold_value !== null && operandSpec.spec_comparison_operator
        ? `${formatInstructionComparisonOperator(operandSpec.spec_comparison_operator)} ${formatThreshold(operandSpec.spec_threshold_value)}`
        : undefined;

    items.push({
      dataTestId: `evaluated-spec-${groupId}-operand-${operand}`,
      indentLevel,
      label: formatInstructionOperand(operand, t),
      secondaryValue: operandSecondaryValue,
      showValueStatusIcon: Boolean(operandSecondaryValue),
      specId: operandSpec?.id,
      value: formatPercentageFeature(operandValue),
      valueColor: operandSecondaryValue ? (operandSpec?.spec_triggered ? 'error' : 'success') : undefined,
      variant: 'standard',
    });
  });
};

export const buildEvaluatedSpecsItems = ({
  specs,
  composition,
  hideZeroOrSmallValues = false,
  t,
  language,
}: {
  specs: EvaluatedSpecificationLike[];
  composition?: EvaluatedSpecificationComposition;
  hideZeroOrSmallValues?: boolean;
  language?: I18nLanguage;
  t?: FeaturesTranslate;
}) => {
  const items: SpecItem[] = [];
  const specificationOrderById = new Map(specs.map((specification, index) => [specification.id, index]));

  const sortSpecificationsByPosition = (
    leftSpecification: EvaluatedSpecificationLike,
    rightSpecification: EvaluatedSpecificationLike
  ) => {
    if (leftSpecification.position !== rightSpecification.position) {
      return leftSpecification.position - rightSpecification.position;
    }

    return (
      (specificationOrderById.get(leftSpecification.id) ?? 0) - (specificationOrderById.get(rightSpecification.id) ?? 0)
    );
  };

  const groupSpecs = specs.filter(isRootSpecification);
  const childSpecs = specs.filter((specification) => !isRootSpecification(specification));

  const groupSpecById = new Map(groupSpecs.map((specification) => [specification.id, specification]));

  const operandSpecMapByParentId = new Map<number, Map<Operand, EvaluatedSpecificationLike>>();
  const subGroupSpecsByParentId = new Map<number, EvaluatedSpecificationLike[]>();

  childSpecs.forEach((childSpecification) => {
    if (childSpecification.parent_spec_id == null) {
      return;
    }

    const parentSpecification = groupSpecById.get(childSpecification.parent_spec_id);
    if (!parentSpecification) {
      return;
    }

    const childSpecificationKind = getChildSpecificationKind({
      childSpecification,
      reportInvalid: true,
    });

    if (childSpecificationKind === ChildSpecType.direct_child) {
      const childOperand = childSpecification.operands[0];
      if (!operandSpecMapByParentId.has(parentSpecification.id)) {
        operandSpecMapByParentId.set(parentSpecification.id, new Map());
      }
      operandSpecMapByParentId.get(parentSpecification.id)?.set(childOperand, childSpecification);
      return;
    }

    if (childSpecificationKind !== ChildSpecType.sub_group) {
      return;
    }

    if (!subGroupSpecsByParentId.has(parentSpecification.id)) {
      subGroupSpecsByParentId.set(parentSpecification.id, []);
    }
    subGroupSpecsByParentId.get(parentSpecification.id)?.push(childSpecification);
  });

  const sortedGroupSpecs = [...groupSpecs].sort(sortSpecificationsByPosition);
  const sortedSubGroupSpecsByParentId = new Map<number, EvaluatedSpecificationLike[]>(
    Array.from(subGroupSpecsByParentId.entries()).map(([parentSpecificationId, subGroupSpecifications]) => [
      parentSpecificationId,
      [...subGroupSpecifications].sort(sortSpecificationsByPosition),
    ])
  );

  const appendSubGroupSpecifications = (parentSpecificationId: number) => {
    const subGroupSpecifications = sortedSubGroupSpecsByParentId.get(parentSpecificationId) ?? [];

    subGroupSpecifications.forEach((subGroupSpecification) => {
      const subGroupValuePresentation = getValuePresentation(subGroupSpecification.spec_value);
      const subGroupSecondaryValue = getSpecificationSecondaryValue(subGroupSpecification);
      const subGroupLabel =
        subGroupSpecification.name && subGroupSpecification.name.trim() !== ''
          ? subGroupSpecification.name
          : formatOperandsList(subGroupSpecification.operands, { language, t });
      const shouldRenderSubGroup = !hideZeroOrSmallValues || subGroupValuePresentation.hasVisibleValue;

      if (shouldRenderSubGroup) {
        items.push({
          dataTestId: `evaluated-spec-${subGroupSpecification.id}`,
          indentLevel: INDENT_LEVEL.subGroup,
          label: subGroupLabel,
          secondaryValue: subGroupSecondaryValue,
          showValueStatusIcon: Boolean(subGroupSecondaryValue && subGroupValuePresentation.hasVisibleValue),
          specId: subGroupSpecification.id,
          value: subGroupValuePresentation.value,
          valueColor: subGroupValuePresentation.hasVisibleValue
            ? subGroupSpecification.spec_triggered
              ? 'error'
              : 'success'
            : undefined,
          variant: 'bold',
        });
        addSpecificationOperands({
          composition,
          groupId: subGroupSpecification.id,
          hideZeroOrSmallValues,
          indentLevel: INDENT_LEVEL.subGroupOperand,
          items,
          operands: subGroupSpecification.operands,
          t,
        });
      }
    });
  };

  sortedGroupSpecs.forEach((specification) => {
    const groupStartIndex = items.length;
    const specificationValue = typeof specification.spec_value === 'number' ? specification.spec_value : null;
    const groupValuePresentation = getValuePresentation(specificationValue);
    const specificationSecondaryValue = getSpecificationSecondaryValue(specification);
    const shouldRenderGroup = !hideZeroOrSmallValues || groupValuePresentation.hasVisibleValue;

    if (specification.operands.length === 1) {
      const specificationLabel =
        specification.name && specification.name.trim() !== ''
          ? specification.name
          : formatInstructionOperand(specification.operands[0], t);

      if (shouldRenderGroup) {
        items.push({
          dataTestId: `evaluated-spec-${specification.id}`,
          indentLevel: INDENT_LEVEL.group,
          label: specificationLabel,
          secondaryValue: specificationSecondaryValue,
          showValueStatusIcon: Boolean(specificationSecondaryValue && groupValuePresentation.hasVisibleValue),
          specId: specification.id,
          value: groupValuePresentation.value,
          valueColor: groupValuePresentation.hasVisibleValue
            ? specification.spec_triggered
              ? 'error'
              : 'success'
            : undefined,
          variant: 'bold',
        });
      }

      const operand = specification.operands[0];
      const operandValue = getOperandValue(operand, composition);
      if (
        shouldRenderGroup &&
        operandValue !== null &&
        (!hideZeroOrSmallValues || !isZeroOrSmallForDisplay(operandValue))
      ) {
        items.push({
          dataTestId: `evaluated-spec-${specification.id}-operand-${operand}`,
          indentLevel: INDENT_LEVEL.groupOperand,
          isLastInGroup: true,
          label: formatInstructionOperand(operand, t),
          value: formatPercentageFeature(operandValue),
          variant: 'standard',
        });
      }

      if (shouldRenderGroup) {
        appendSubGroupSpecifications(specification.id);
      }
      if (items.length > groupStartIndex) {
        items[items.length - 1].isLastInGroup = true;
      }

      return;
    }

    const specificationLabel =
      specification.name && specification.name.trim() !== ''
        ? specification.name
        : formatOperandsList(specification.operands, { language, t });

    if (shouldRenderGroup) {
      items.push({
        dataTestId: `evaluated-spec-${specification.id}`,
        indentLevel: INDENT_LEVEL.group,
        label: specificationLabel,
        secondaryValue: specificationSecondaryValue,
        showValueStatusIcon: Boolean(specificationSecondaryValue && groupValuePresentation.hasVisibleValue),
        specId: specification.id,
        value: groupValuePresentation.value,
        valueColor: groupValuePresentation.hasVisibleValue
          ? specification.spec_triggered
            ? 'error'
            : 'success'
          : undefined,
        variant: 'bold',
      });
    }

    if (shouldRenderGroup) {
      addSpecificationOperands({
        composition,
        groupId: specification.id,
        hideZeroOrSmallValues,
        indentLevel: INDENT_LEVEL.groupOperand,
        items,
        operandSpecMap: operandSpecMapByParentId.get(specification.id),
        operands: specification.operands,
        t,
      });
      appendSubGroupSpecifications(specification.id);
    }

    if (items.length > groupStartIndex) {
      items[items.length - 1].isLastInGroup = true;
    }
  });

  if (composition) {
    const allSpecOperands = new Set<string>();
    specs.forEach((specification) => {
      specification.operands.forEach((operand) => {
        allSpecOperands.add(operand);
      });
    });

    const compositionEntries = Object.entries(composition)
      .reduce<CompositionEntry[]>((entries, [operand, value]) => {
        if (!isOperandKey(operand)) return entries;
        if (typeof value !== 'number') return entries;
        if (hideZeroOrSmallValues && isZeroOrSmallForDisplay(value)) return entries;
        if (allSpecOperands.has(operand)) return entries;

        entries.push([operand, value]);
        return entries;
      }, [])
      .sort(([, leftValue], [, rightValue]) => rightValue - leftValue);

    if (compositionEntries.length > 0) {
      const ungroupedMaterialsValue = compositionEntries.reduce((sum, [, value]) => sum + (value as number), 0);

      items.push({
        dataTestId: 'other-materials-title',
        indentLevel: INDENT_LEVEL.group,
        label: t
          ? t('backlog.breakdowns.evaluatedSpecsItems.ungroupedMaterials')
          : translate('backlog.breakdowns.evaluatedSpecsItems.ungroupedMaterials', {
              namespace: 'features',
            }),
        value: formatPercentageFeature(ungroupedMaterialsValue),
        variant: 'bold',
      });

      compositionEntries.forEach(([operand, value]) => {
        items.push({
          dataTestId: `composition-operand-${operand}`,
          indentLevel: INDENT_LEVEL.ungroupedOperand,
          label: formatInstructionOperand(operand, t),
          value: formatPercentageFeature(value),
          variant: 'standard',
        });
      });

      items[items.length - 1].isLastInGroup = true;
    }
  }

  return items;
};
